import { touchVenueDashboardActivity } from '../utils/venueDashboardSession';
import { looksInternalError, toCustomerError } from '../utils/customerError';
import { API_BASE_URL } from '../config/apiEndpoints';

const CUSTOMER_PAYMENT_ERRORS = {
  network: 'We couldn’t load this page. Check your connection and try again.',
  unavailable: 'Payment is temporarily unavailable. Please try again in a moment.',
  session: 'Please sign in to continue.',
  forbidden: 'Sign in with the email address used for this booking, or open the link from your booking email.',
  expiredLink: 'This payment link has expired. Open the latest link from your booking email, or sign in to Occasia.',
  invalidLink: 'This payment link is invalid or has expired.',
  notFound: 'We couldn’t find this booking.',
  generic: 'Something went wrong. Please try again shortly.'
};

export const toCustomerPaymentError = (message, { status } = {}) => {
  const value = String(message || '').trim();
  const lower = value.toLowerCase();
  if (status === 401 || lower.includes('invalid token') || lower.includes('token has expired')) {
    if (
      lower.includes('payment link') ||
      lower.includes('secure payment') ||
      lower.includes('token')
    ) {
      return CUSTOMER_PAYMENT_ERRORS.expiredLink;
    }
    return CUSTOMER_PAYMENT_ERRORS.session;
  }
  if (status === 403 || lower.includes('not allowed') || lower.includes('not authorised')) {
    return CUSTOMER_PAYMENT_ERRORS.forbidden;
  }
  if (status === 404) return CUSTOMER_PAYMENT_ERRORS.notFound;
  if (status === 502 || status === 503 || status === 504 || status >= 500) {
    return CUSTOMER_PAYMENT_ERRORS.unavailable;
  }
  if (
    lower.includes('failed to fetch') ||
    lower.includes('network') ||
    lower.includes('unable to reach')
  ) {
    return CUSTOMER_PAYMENT_ERRORS.network;
  }
  if (value && !looksInternalError(value)) return value;
  return toCustomerError(value, { status, fallback: CUSTOMER_PAYMENT_ERRORS.generic });
};

class PaymentsApiService {
  constructor() {
    this._paymentToken = null;
  }

  setPaymentToken(token) {
    this._paymentToken = typeof token === 'string' ? token.trim() : null;
  }

  clearPaymentToken() {
    this._paymentToken = null;
  }

  getBaseUrl() {
    return API_BASE_URL;
  }

  getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  getBookingAuthHeaders() {
    if (this._paymentToken) {
      return { 'X-Occasia-Payment-Token': this._paymentToken };
    }
    return this.getAuthHeaders();
  }

  async parseError(response) {
    let detail = '';
    try {
      const errorData = await response.json();
      detail = errorData.detail || errorData.message || errorData.error || '';
      if (typeof detail === 'object' && detail !== null) {
        detail = detail.message ? String(detail.message) : '';
      }
    } catch {
      // ignore non-json responses
    }

    const detailStr = typeof detail === 'string' ? detail.trim() : '';
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('Payments API error', {
        status: response.status,
        path: response.url,
        detail: detailStr || detail
      });
    }
    return toCustomerPaymentError(detailStr, { status: response.status });
  }

  async request(path, { method = 'GET', body, authenticated = true } = {}) {
    const usingPaymentToken = Boolean(this._paymentToken);
    if (authenticated) {
      touchVenueDashboardActivity();
    }
    if (authenticated && !usingPaymentToken && !this.getAuthHeaders().Authorization) {
      throw new Error(CUSTOMER_PAYMENT_ERRORS.session);
    }

    const headers = {
      ...(authenticated ? this.getBookingAuthHeaders() : {})
    };
    const hasBody = body !== undefined && body !== null;
    if (hasBody) {
      headers['Content-Type'] = 'application/json';
    }

    let response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers,
        ...(hasBody ? { body: JSON.stringify(body) } : {})
      });
    } catch (error) {
      const message = String(error?.message || '').toLowerCase();
      const isNetworkLike =
        message.includes('failed to fetch') ||
        message.includes('networkerror') ||
        message.includes('load failed') ||
        message.includes('network request failed');
      if (isNetworkLike) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('Payments API network error', { method, path, message: error?.message });
        }
        throw new Error(CUSTOMER_PAYMENT_ERRORS.network);
      }
      throw error;
    }
    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }
    if (response.status === 204) return {};
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  /**
   * Frontend bootstrap key for Stripe.js.
   */
  async getStripeConfig() {
    return this.request('/payments/config', { authenticated: false });
  }

  /**
   * Start Stripe Connect OAuth (or resume Account Link if already linked).
   */
  async startConnectOAuth(venueId, { returnUrl, refreshUrl } = {}) {
    if (!venueId) throw new Error('venueId is required');
    const body = { venue_id: venueId };
    if (returnUrl) body.return_url = returnUrl;
    if (refreshUrl) body.refresh_url = refreshUrl;
    return this.request('/payments/connect/oauth/start', {
      method: 'POST',
      body
    });
  }

  /**
   * Return existing connected account mapping (legacy sync — prefer startConnectOAuth).
   */
  async ensureConnectAccount(venueId, { email } = {}) {
    if (!venueId) throw new Error('venueId is required');
    const body = { venue_id: venueId };
    const normalizedEmail = typeof email === 'string' ? email.trim() : '';
    if (normalizedEmail) {
      // Send common alias keys so backend can pick the expected field name.
      body.email = normalizedEmail;
      body.stripe_email = normalizedEmail;
      body.account_email = normalizedEmail;
    }
    return this.request('/payments/connect/accounts', {
      method: 'POST',
      body
    });
  }

  /**
   * Get connect status for venue payouts.
   */
  async getConnectStatus(venueId) {
    if (!venueId) throw new Error('venueId is required');
    return this.request(`/payments/connect/status?venue_id=${encodeURIComponent(String(venueId))}`);
  }

  /**
   * Hosted onboarding link for Stripe Connect Express.
   */
  async createOnboardingLink(venueId, { refreshUrl, returnUrl } = {}) {
    const body = this.buildOnboardingLinkPayload(venueId, { refreshUrl, returnUrl });
    return this.request('/payments/connect/onboarding-link', {
      method: 'POST',
      body
    });
  }

  /**
   * Hosted onboarding link with optional explicit email hint.
   */
  async createOnboardingLinkWithEmail(venueId, { refreshUrl, returnUrl, email } = {}) {
    const body = this.buildOnboardingLinkPayload(venueId, { refreshUrl, returnUrl, email });
    return this.request('/payments/connect/onboarding-link', {
      method: 'POST',
      body
    });
  }

  /**
   * Build onboarding payload used by frontend and debug previews.
   */
  buildOnboardingLinkPayload(venueId, { refreshUrl, returnUrl, email } = {}) {
    if (!venueId) throw new Error('venueId is required');
    const body = { venue_id: venueId };
    if (refreshUrl) body.refresh_url = refreshUrl;
    if (returnUrl) body.return_url = returnUrl;
    const normalizedEmail = typeof email === 'string' ? email.trim() : '';
    if (normalizedEmail) {
      body.email = normalizedEmail;
      body.stripe_email = normalizedEmail;
      body.account_email = normalizedEmail;
    }
    return body;
  }

  /**
   * Express dashboard link for connected venue owner.
   */
  async createDashboardLink(venueId, { returnUrl } = {}) {
    if (!venueId) throw new Error('venueId is required');
    const body = { venue_id: venueId };
    if (returnUrl) body.return_url = returnUrl;
    return this.request('/payments/connect/dashboard-link', {
      method: 'POST',
      body
    });
  }

  /**
   * Guest checkout summary (payment token or logged-in JWT).
   */
  async getGuestCheckout(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(
      `/bookings/${encodeURIComponent(String(bookingId))}/payments/guest-checkout`
    );
  }

  /**
   * Download URL for invoice using guest payment token.
   */
  async getGuestInvoiceDownloadUrl(bookingId, fileId) {
    if (!bookingId || !fileId) throw new Error('bookingId and fileId are required');
    return this.request(
      `/bookings/${encodeURIComponent(String(bookingId))}/owner-invoices/${encodeURIComponent(String(fileId))}/guest-download-url`
    );
  }

  /**
   * SetupIntent used to collect organizer payment method.
   */
  async createSetupIntent(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(`/bookings/${encodeURIComponent(String(bookingId))}/payments/setup-intent`, {
      method: 'POST',
      body: {}
    });
  }

  /**
   * Confirm that setup intent + payment method are attached.
   */
  async confirmPaymentMethodAttached(bookingId, payload) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(`/bookings/${encodeURIComponent(String(bookingId))}/payments/attach-method`, {
      method: 'POST',
      body: payload
    });
  }

  /**
   * Current payment status for booking.
   */
  async getBookingPaymentStatus(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(`/bookings/${encodeURIComponent(String(bookingId))}/payments/status`);
  }

  /**
   * Whether Pay with Stripe should be shown for this booking.
   */
  async getBookingPaymentEligibility(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(
      `/bookings/${encodeURIComponent(String(bookingId))}/payments/eligibility`
    );
  }

  /**
   * Optional: trigger hosted setup/retry flow if backend exposes it.
   */
  async createPaymentSetupSession(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(`/bookings/${encodeURIComponent(String(bookingId))}/payments/setup-session`, {
      method: 'POST'
    });
  }

  /**
   * Optional: retry payment action-required flow.
   */
  async retryPayment(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    return this.request(`/bookings/${encodeURIComponent(String(bookingId))}/payments/retry`, {
      method: 'POST'
    });
  }
}

const paymentsApiService = new PaymentsApiService();
export default paymentsApiService;
